import { FC, useState } from 'react'
import styled from 'styled-components'
import { ArrowRight } from 'svgIcons'
import { useFallbackImageInSSR } from 'utils'
import {
  useCurrentUserQuery,
  useFollowChannelMutation,
} from 'generated/graphql'
import Button from '@app.components/Button'
import useChannelError from '@app.feature/channel/hook/useChannelError'
import { ChannelUserType } from 'app.constants/enums'
import { mobileBreakPoint } from 'app.styled/media'
import LoginAlert from 'components/LoginAlert'
import NewsReactionUserModal, { defaultImage } from './NewsReactionUserModal'

export type UserCardItem = {
  id: string
  type: ChannelUserType.CHANNEL | ChannelUserType.OWNER | ChannelUserType.NORMAL
  name: string
  description: string
  channelUrl: string
  imageUrl: string
  viewerIsOwner: boolean
  viewerHasFollowed: boolean
  currentTab: 'like' | 'scrap'
  newsItem: {
    id: string
    content: string
    newsItemUrl: string
  }[]
}

type Tprops = {
  data: UserCardItem
}

const NewsReactionCard: FC<Tprops> = ({ data }) => {
  const onError = useChannelError()
  const followChannelMutation = useFollowChannelMutation({ onError })
  const [userModalOpen, setUserModalOpen] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const { data: currentUser } = useCurrentUserQuery()

  const fallbackImageProps = useFallbackImageInSSR(defaultImage[data.type])

  const handleFollowBtn = () => {
    if (!currentUser?.currentUser) return setLoginModalOpen(true)

    if (!data.viewerHasFollowed) {
      const [mutate] = followChannelMutation
      mutate({ variables: { channelId: data.id } })
    }
  }

  const handleCardClick = () => {
    if (data.type === ChannelUserType.OWNER) return

    setUserModalOpen(true)
  }

  return (
    <StyledWrapper>
      <div
        className="lt"
        onClick={handleCardClick}
        data-cursor={data.type !== ChannelUserType.OWNER}
      >
        <img
          className="logo-img"
          src={data.imageUrl}
          alt="프로필 사진"
          data-channel={data.type === ChannelUserType.CHANNEL}
          {...fallbackImageProps}
        />
        <div>
          <div className="user-content">
            <div className="name ellipsis">{data.name}</div>
            {data.type !== ChannelUserType.OWNER && <ArrowRight />}
          </div>
          {data.type === ChannelUserType.CHANNEL && (
            <div className="desc ellipsis">{data.description}</div>
          )}
        </div>
      </div>
      {data.type === ChannelUserType.CHANNEL && !data.viewerIsOwner && (
        <Button
          className="follow-btn"
          onClick={handleFollowBtn}
          disabled={data.viewerHasFollowed}
          color={data.viewerHasFollowed ? 'lightGrey2' : 'primary'}
          variant={data.viewerHasFollowed ? 'contained' : 'outlined'}
        >
          {data.viewerHasFollowed ? '팔로우중' : '+팔로우'}
        </Button>
      )}
      {userModalOpen && (
        <NewsReactionUserModal
          data={data}
          open={userModalOpen}
          onClose={() => setUserModalOpen(false)}
        />
      )}
      {loginModalOpen && (
        <LoginAlert
          DialogProps={{ open: loginModalOpen }}
          onRequestClose={() => setLoginModalOpen(false)}
        />
      )}
    </StyledWrapper>
  )
}

export default NewsReactionCard

const StyledWrapper = styled.div`
  min-height: 69px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  .lt {
    display: flex;
    align-items: center;
    gap: 16px;
    &[data-cursor='true'] {
      cursor: pointer;
    }
    .logo-img {
      width: 48px;
      height: 48px;
      background-color: #eee;
      border-radius: 50%;
      object-fit: cover;
      &[data-channel='true'] {
        border-radius: 5px;
        border: 1px solid #ddd;
      }
    }
    .user-content {
      display: flex;
      align-items: center;
    }
    .name {
      font-size: 16px;
      font-weight: 500;
    }
    .desc {
      font-size: 12px;
      font-weight: 500;
      margin-top: 2px;
    }
    svg {
      margin: 2px 0 0 2px;
      width: 11px;
      height: 11px;
    }
  }
  .follow-btn {
    width: 78px;
    height: 36px;
    font-size: 14px;
    font-weight: bold;
    flex-shrink: 0;
  }
  .ellipsis {
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ${mobileBreakPoint} {
    .lt {
      .name {
        font-size: 14px;
      }
      .desc {
        font-weight: 400;
      }
    }
    .ellipsis {
      max-width: 160px;
    }
  }
`
